import {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCalendlyEventListener } from "react-calendly";
import autosize from "autosize";
import { useDebouncedCallback } from "use-debounce";
import { Output } from "valibot";

import ChatBasedIntegration from "components/Integration";
import MessageBubble from "components/MessageBubble";
import PopupMessages from "components/PopupMessages";
import {
  BotInfoSchema,
  ChatDataSchema,
  ChatUserFormFieldsSchema,
  InstalledApps,
  LocationBasedConsentInfo,
  StarterQuestionSchema,
  StarterQuestionsSchema,
} from "components/SonicBot/schemas";
import UserDataForm from "components/UserDataForm";
import { ChatUserFormData, createUser, requestCallback } from "shared/api";
import {
  getLocalStorageValue,
  getUserDataStorageKey,
  getUserFormStorageKey,
  setLocalStorageValue,
} from "shared/functions";
import { useConfigContext } from "shared/hooks";
import { IntegrationHandler } from "shared/integrations";
import { IntegrationType } from "shared/integrations/configs";
import { isShowLocationBasedConsentForm } from "shared/integrations/utils";
import { AppConfiguration, GenerationMode } from "shared/types";
import classNames from "shared/utils/classNames";
import { getChatPanelText } from "shared/utils/sourceUtils";
import botsonicLogoGrey from "assets/images/botsonic-logo-grey.svg";

import Integrations from "./Integrations";
import IntegrationInlineWidget from "./Integrations/IntegrationInlineWidget";
import LocationBasedConsentForm from "./LocationBasedConsentForm";
import SendButton from "./SendButton";
import TopBar from "./TopBar";

import "regenerator-runtime";
import "./index.css";

type Props = {
  botInfo: Output<typeof BotInfoSchema>;
  onSend: (message: string, chatUserId?: string) => void;
  generationMode: GenerationMode;
  setGenerationMode: Dispatch<SetStateAction<GenerationMode>>;
  message: string;
  chatData: Output<typeof ChatDataSchema>;
  setChatData: Dispatch<SetStateAction<any[]>>;
  setMessage: Dispatch<SetStateAction<string>>;
  resetChat: () => void;
  useAsPreview: boolean;
  starterQuestions: Output<typeof StarterQuestionsSchema>;
  chatId: string;
  config: AppConfiguration;
  eventSource: EventSource | null;
  chatUserFormFields: Output<typeof ChatUserFormFieldsSchema>;
  loadingChatUserFormFields: boolean;
  baseURL: string;
  token: string;
  widgetPosition: string | undefined;
  messageLengthWhenSkipped: number;
  setMessageLengthWhenSkipped: Dispatch<SetStateAction<number>>;
  onSuggestionClick: (
    starterQuestion: Output<typeof StarterQuestionSchema>,
  ) => Promise<void>;
  onIntegrationClick: (integration: IntegrationHandler) => Promise<void>;
  locationBasedConsentInfo?: Output<typeof LocationBasedConsentInfo>;
  installedApps: Output<typeof InstalledApps>;
};

function ChatPanel(props: Props) {
  const { isIframe, instanceName, autofocusOff } = useConfigContext();

  const {
    botInfo,
    onSend,
    generationMode,
    setGenerationMode,
    message,
    chatData,
    setChatData,
    setMessage,
    resetChat,
    useAsPreview,
    starterQuestions,
    chatId,
    config,
    eventSource,
    chatUserFormFields,
    loadingChatUserFormFields,
    baseURL,
    token,
    widgetPosition = "right",
    onSuggestionClick,
    onIntegrationClick,
    locationBasedConsentInfo,
    messageLengthWhenSkipped,
    setMessageLengthWhenSkipped,
    installedApps,
  } = props;

  const [integrationLink, setIntegrationLink] = useState<string | null>(null);
  const [isIdentifiedUser, setIsIdentifiedUser] = useState<boolean>(false);
  const [chatUser, setChatUser] = useState<ChatUserFormData | undefined>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setRenderCalendly] = useState(false);
  const [showLocationBasedConsentForm, setShowLocationBasedConsentForm] =
    useState<boolean>(false);
  const [openIntegrationIds, setOpenIntegrationIds] = useState<string[]>([]);
  const [hideInputBar, setHideInputBar] = useState<boolean>(false);
  const [showRequestCallbackForm, setShowRequestCallbackForm] =
    useState<boolean>(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const copyTexts = getChatPanelText(
    botInfo.bot_prompt_config.generation_language,
  );

  const handleSubmit = () => {
    if (!message || message === " ") {
      return;
    }
    if (!generationMode) {
      onSend(message, chatUser?._id || undefined);
    }
  };

  useEffect(() => {
    setShowLocationBasedConsentForm(
      isShowLocationBasedConsentForm(botInfo.id, locationBasedConsentInfo),
    );
  }, [locationBasedConsentInfo, botInfo.id]);

  useEffect(() => {
    // set auto size to textarea
    const questionEls = document.querySelectorAll(".message");
    for (const questionEl of questionEls) {
      autosize(questionEl);
    }

    // checking if user identified
    const isIdentified =
      getLocalStorageValue(getUserDataStorageKey(instanceName, botInfo.id)) ||
      "null";
    const identifiedUser: ChatUserFormData = JSON.parse(isIdentified);
    if (identifiedUser) {
      setIsIdentifiedUser(true);
      setChatUser(identifiedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Once the user writes the message, it should again focus the cursor in the textbox so user can keep typing
    if (!generationMode && !autofocusOff) {
      inputRef?.current?.focus();
    }
  }, [generationMode, autofocusOff]);

  useLayoutEffect(() => {
    if (inputRef.current) {
      if (!message) {
        inputRef.current.style.height = "24px";
      }
      autosize(inputRef.current);
    }
  }, [message]);

  useEffect(() => {
    // reset height that is changed automatically according to content
    if (message) {
      return;
    }
    const questionEls = document.querySelectorAll(".message");
    for (const questionEl of questionEls) {
      const el = questionEl as HTMLTextAreaElement;
      el.style.height = "";
      el.style.overflow = "hidden";
    }
  }, [message]);

  const scrollDown = useDebouncedCallback(
    () =>
      messagesContainerRef.current?.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth",
      }),
    500,
    {
      maxWait: 500,
      leading: true,
    },
  );

  useCalendlyEventListener({
    onEventScheduled: () => {
      setIntegrationLink(null);
      setRenderCalendly(false);
      setChatData([
        ...chatData,
        {
          message:
            "Meeting scheduled.\nYou can find a mail with the meeting details in your inbox.",
          sent: false,
        },
      ]);
    },
  });

  useLayoutEffect(() => {
    const getIntegrationLink = () => {
      const lastItem = chatData.at(-1);
      if (lastItem?.integrationData?.type === IntegrationType.CALENDLY) {
        return lastItem.integrationData.link || null;
      }
      return null;
    };

    setIntegrationLink(getIntegrationLink());
    setRenderCalendly(true);
  }, [chatData, setRenderCalendly]);

  const isShowUserRequestCallbackButton = useMemo(() => {
    return installedApps?.some(
      app => app.config_type === "request_callback" && app.is_active,
    );
  }, [installedApps]);

  useLayoutEffect(() => {
    if (!chatData.length) {
      return;
    }

    scrollDown();
  }, [scrollDown, chatData]);

  const hasFormItems =
    chatUserFormFields &&
    Array.isArray(chatUserFormFields?.data_fields) &&
    !!chatUserFormFields?.data_fields?.filter(item => !item.is_deleted).length;
  const storedFormConfiguration = getLocalStorageValue(
    getUserFormStorageKey(instanceName, botInfo.id),
  );
  const isFormConfigurationChanged =
    chatUserFormFields &&
    storedFormConfiguration &&
    JSON.stringify(chatUserFormFields) !== storedFormConfiguration;

  const suppressUserForm =
    isIdentifiedUser || config.hideUserForm || !hasFormItems;

  const showUserDataForm =
    showRequestCallbackForm ||
    (!botInfo.bot_info_config.hide_user_form &&
      (isFormConfigurationChanged || !suppressUserForm));

  const classNameChatPanelContainer = useMemo(() => {
    if (isIframe) {
      return "flex-1 flex flex-col h-screen md:h-[calc(100%-4rem)] relative";
    }
    return classNames(
      useAsPreview ? "chat-panel-content-preview" : "",
      "max-h-full xs:max-h-[704px]",
      "chat-panel-content",
    );
  }, [isIframe, useAsPreview]);

  if (integrationLink) {
    return (
      <div className={classNameChatPanelContainer}>
        <IntegrationInlineWidget
          chatbotColor={botInfo.bot_info_config.chatbot_color}
          chatData={chatData}
          integrationLink={integrationLink}
          setIntegrationLink={setIntegrationLink}
        />
      </div>
    );
  }

  const handleAcceptConsentAgreement = () => {
    if (!locationBasedConsentInfo) {
      return;
    }
    setLocalStorageValue(
      `consentInfo__${botInfo.id}`,
      JSON.stringify({
        isConsentGiven: true,
        lastUpdatedAt: locationBasedConsentInfo.updated_at,
      }),
    );
    setShowLocationBasedConsentForm(false);
  };
  const handleOnIntegrationClick = async (
    integration: IntegrationHandler,
    chatId?: string,
  ): Promise<void> => {
    if (integration.type === "zendesk" || integration.type === "freshdesk") {
      if (chatId) {
        setOpenIntegrationIds(id => [...id, chatId]);
        setHideInputBar(true);
      }
      return;
    }
    return onIntegrationClick(integration);
  };
  const handleOnChatBasedIntegrationFinish = () => {
    setHideInputBar(false);
  };

  const handleChatUserCreation = async (formData: any) => {
    const response = await createUser({ baseURL, token, data: formData });
    setLocalStorageValue(
      getUserDataStorageKey(instanceName, botInfo.id),
      JSON.stringify(response),
    );
    setLocalStorageValue(
      getUserFormStorageKey(instanceName, botInfo.id),
      JSON.stringify(chatUserFormFields),
    );
    setChatUser(response);
    setIsIdentifiedUser(true);
    if (showRequestCallbackForm) {
      requestCallback({
        token,
        chatUserFormDataId: response?._id || "",
        chatId,
      });
    }
    setShowRequestCallbackForm(false);
  };

  const handleOnRequestCallbackClick = () => {
    if (storedFormConfiguration) {
      requestCallback({
        token,
        chatUserFormDataId: chatUser?._id || "",
        chatId,
      });
      const message = copyTexts.formAlreadySubmittedMessage;
      setChatData([
        ...chatData,
        {
          message,
          sent: false,
          showLikeDislikeBtn: false,
        },
      ]);
    } else {
      setShowRequestCallbackForm(true);
    }
  };

  return (
    <div className={classNameChatPanelContainer}>
      <TopBar
        botInfo={botInfo}
        messagesLength={chatData?.length || 0}
        startAgainLabel={copyTexts.start_again}
        copyTexts={copyTexts}
        resetChat={resetChat}
        config={config}
        chatId={chatId}
        messageLengthWhenSkipped={messageLengthWhenSkipped}
        setMessageLengthWhenSkipped={setMessageLengthWhenSkipped}
        showRequestCallbackButton={isShowUserRequestCallbackButton}
        handleOnRequestCallbackClick={handleOnRequestCallbackClick}
      />

      {showLocationBasedConsentForm && (
        <div className={classNames("message-container")}>
          <div className="message-content">
            <LocationBasedConsentForm
              message={locationBasedConsentInfo?.message || ""}
              onAccept={handleAcceptConsentAgreement}
              botAvatar={botInfo.bot_info_config?.chatbot_avatar}
              copyTexts={copyTexts}
            />
          </div>
        </div>
      )}
      {showUserDataForm && !showLocationBasedConsentForm && (
        <UserDataForm
          botInfo={botInfo}
          chatUserFormFields={chatUserFormFields}
          loadingChatUserFormFields={loadingChatUserFormFields}
          copyTexts={copyTexts}
          handleChatUserCreation={handleChatUserCreation}
        />
      )}

      {!showUserDataForm && !showLocationBasedConsentForm && (
        <>
          <div
            ref={messagesContainerRef}
            className={classNames(
              "message-container",
              isIframe && "flex-1 p-0 md:p-4",
            )}
          >
            {!chatData?.length && (
              <MessageBubble
                id="-1"
                botInfo={botInfo}
                isSent={false}
                message={
                  botInfo.bot_info_config.welcome_message ||
                  copyTexts.default_welcome_message
                }
                config={config}
                chatId={chatId}
                chatData={chatData}
                setChatData={setChatData}
                copyTexts={copyTexts}
              />
            )}
            <div className="message-content">
              {chatData.map(
                (
                  {
                    id,
                    message,
                    sent,
                    sources,
                    integration = [],
                    showLikeDislikeBtn = true,
                  },
                  idx,
                ) => {
                  const integrationData = integration[0] as IntegrationHandler;
                  if (
                    (integrationData?.type === "zendesk" ||
                      integrationData?.type === "freshdesk") &&
                    openIntegrationIds.includes(id)
                  ) {
                    return (
                      <ChatBasedIntegration
                        key={id}
                        integration={integrationData}
                        generationLanguage={
                          botInfo.bot_prompt_config.generation_language
                        }
                        chatId={chatId}
                        token={token}
                        botAvatar={botInfo.bot_info_config.chatbot_avatar}
                        onFinish={handleOnChatBasedIntegrationFinish}
                      />
                    );
                  }
                  return (
                    <MessageBubble
                      key={idx.toString()}
                      id={id || `${idx}`}
                      botInfo={botInfo}
                      isSent={sent}
                      message={message}
                      sources={sources}
                      config={config}
                      chatId={chatId}
                      chatData={chatData}
                      setChatData={setChatData}
                      isLastMessage={chatData.length - 1 === idx}
                      copyTexts={copyTexts}
                      showLikeDislikeBtn={showLikeDislikeBtn}
                    />
                  );
                },
              )}
            </div>
            {!chatData.length && !!starterQuestions?.length && (
              <div className="suggestion-content">
                <PopupMessages
                  botInfo={botInfo}
                  starterQuestions={starterQuestions}
                  onSuggestionClick={onSuggestionClick}
                  widgetPosition={widgetPosition}
                  chatDataLength={chatData?.length || 0}
                  isOverButton={false}
                  generationMode={generationMode}
                />
              </div>
            )}
            <Integrations
              botInfo={botInfo}
              chatData={chatData}
              config={config}
              generationMode={generationMode}
              onIntegrationClick={handleOnIntegrationClick}
              renderedChatId={openIntegrationIds}
            />
          </div>
          {!hideInputBar && (
            <div
              className={classNames(
                "relative",
                isIframe ? "form-container_Iframe" : "form-container",
              )}
            >
              <textarea
                id="message_input"
                ref={inputRef}
                className={classNames(
                  "w-full resize-none appearance-none rounded-3xl border-0 py-2 pl-3 pr-10 text-base outline-none",
                  isIframe && "py-0 pl-1 placeholder:text-base",
                )}
                value={generationMode ? "..." : message}
                onChange={(e: any) => setMessage(e?.target?.value ?? "")}
                disabled={!!generationMode}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    onSend(
                      (e.target as HTMLInputElement).value,
                      chatUser ? chatUser._id : undefined,
                    );
                  }
                }}
                placeholder={
                  botInfo.bot_info_config.custom_placeholder ||
                  "Send a message..."
                }
                rows={1}
              />

              <SendButton
                handleSubmit={handleSubmit}
                generationMode={generationMode}
                setGenerationMode={setGenerationMode}
                eventSource={eventSource}
                disabled={!message}
              />
            </div>
          )}
        </>
      )}

      {botInfo.bot_info_config.is_show_chat_sonic_brand && (
        <div
          className={classNames(
            "bottom-container",
            isIframe && "rounded-none bg-surface",
          )}
        >
          <a
            className="flex items-center justify-center gap-1 text-sm text-secondary no-underline"
            href="https://writesonic.com/botsonic"
            target="_blank"
            rel="noreferrer"
          >
            <div className="hidden sm:block">
              <p>{copyTexts.bottom_text_cta}</p>
            </div>
            <div className="sm:hidden">
              <p>{copyTexts.bottom_powered_by}</p>
            </div>
            <img src={botsonicLogoGrey} alt="Botsonic Logo" />
            <p className="font-semibold">Botsonic</p>
          </a>
        </div>
      )}
    </div>
  );
}

export default ChatPanel;
